import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../lib/api';
import { PageHeader, Loading, ErrorBox, Badge, Modal, Switch, PillToggleGroup, PercentSlider } from '../components/ui';
import { PlatformLogo } from '../components/PlatformLogo';

// Category enum PATCH /v1/admin/platforms/:id accepts (apps/api/src/modules/admin.ts): food | groceries | pharmacy | more.
const CATEGORIES = [
  { key: 'food', label: 'Food' },
  { key: 'groceries', label: 'Groceries' },
  { key: 'pharmacy', label: 'Pharmacy' },
  { key: 'more', label: 'More' },
];

// Real backend enum (apps/api/src/modules/admin.ts): 'live' | 'hidden' | 'screenshot_only' | 'coming_soon' | 'off'.
// Previously this page checked `p.status === 'active'`, which never matches any real value — every
// card always fell into the same branch (always "warn" tone, always an "Activate" button that never
// actually paused a live platform). Map every real status explicitly instead.
const STATUS: Record<string, { label: string; tone: 'success' | 'warn' | 'danger' | 'neutral' }> = {
  live: { label: 'Live', tone: 'success' },
  screenshot_only: { label: 'Screenshot only', tone: 'warn' },
  coming_soon: { label: 'Coming soon', tone: 'neutral' },
  hidden: { label: 'Hidden', tone: 'neutral' },
  off: { label: 'Off', tone: 'danger' },
};

/**
 * Category pills + the three switches + discount slider for one platform card. Keeps local state
 * so pills/slider feel responsive, but commits every change straight to
 * PATCH /v1/admin/platforms/:id (connectEnabled/screenshotsEnabled/visible/categories/discountPct —
 * all already supported server-side, see apps/api/src/modules/admin.ts).
 */
function PlatformControls({ p, onSave }: { p: any; onSave: (patch: Record<string, unknown>) => void }) {
  const [categories, setCategories] = useState<string[]>(p.categories ?? []);
  const [discountPct, setDiscountPct] = useState<number>(p.discountPct ?? 0);

  useEffect(() => {
    setCategories(p.categories ?? []);
    setDiscountPct(p.discountPct ?? 0);
  }, [p.id, p.categories, p.discountPct]);

  const toggleCategory = (key: string) => {
    const next = categories.includes(key) ? categories.filter((c) => c !== key) : [...categories, key];
    setCategories(next);
    onSave({ categories: next });
  };

  return (
    <div className="space-y-2.5 mb-3 pt-3 border-t" style={{ borderColor: 'var(--border)' }}>
      <PillToggleGroup options={CATEGORIES} value={categories} onToggle={toggleCategory} />
      <Switch checked={!!p.connectEnabled} onChange={(v) => onSave({ connectEnabled: v })} label="Account connection" />
      <Switch checked={!!p.screenshotsEnabled} onChange={(v) => onSave({ screenshotsEnabled: v })} label="Screenshot uploads" />
      <Switch checked={!!p.visible} onChange={(v) => onSave({ visible: v })} label="Visible in app" />
      <PercentSlider
        label="Discount"
        value={discountPct}
        max={30}
        onChange={(v) => {
          setDiscountPct(v);
          onSave({ discountPct: v });
        }}
      />
    </div>
  );
}

export function PlatformsPage() {
  const qc = useQueryClient();
  const platforms = useQuery({ queryKey: ['admin-platforms'], queryFn: () => adminApi.platforms() });
  const update = useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: Record<string, unknown> }) => adminApi.platformUpdate(id, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin-platforms'] }),
  });
  const create = useMutation({
    mutationFn: (name: string) => adminApi.platformCreate({ name }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin-platforms'] });
      setAddOpen(false);
      setNewName('');
    },
  });
  const [addOpen, setAddOpen] = useState(false);
  const [newName, setNewName] = useState('');

  if (platforms.isLoading) return <Loading />;
  if (platforms.error) return <ErrorBox error={platforms.error} />;
  const items = (platforms.data as any)?.items ?? [];

  return (
    <div>
      <PageHeader title="Platforms" subtitle="Delivery apps customers bring carts from" />
      <div className="grid grid-cols-3 gap-4">
        {items.map((p: any) => {
          const status = STATUS[p.status] ?? { label: p.status, tone: 'neutral' as const };
          const isLive = p.status === 'live';
          return (
            <div key={p.id} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <PlatformLogo platform={p.id ?? p.name} url={p.logoUrl} size={32} />
                  <div className="font-semibold">{p.name}</div>
                </div>
                <Badge tone={status.tone}>{status.label}</Badge>
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs mb-3" style={{ color: 'var(--text-muted)' }}>
                <div>Carts this week: <b>{p.stats?.cartsWeek}</b></div>
                <div>Avg savings: <b>{p.stats?.avgSavingsPct}%</b></div>
                <div>Connected customers: <b>{p.stats?.connectedCustomers}</b></div>
                <div>Connection health: <b>{p.stats?.uptime}</b></div>
              </div>
              <PlatformControls p={p} onSave={(patch) => update.mutate({ id: p.id, patch })} />
              <div className="flex gap-2">
                <button className="btn-outline" onClick={() => update.mutate({ id: p.id, patch: { visible: !isLive } })} disabled={update.isPending}>
                  {isLive ? 'Pause' : 'Activate'}
                </button>
              </div>
            </div>
          );
        })}
        <button
          className="card p-4 flex flex-col items-center justify-center gap-2 border-dashed min-h-[140px]"
          style={{ color: 'var(--text-faint)' }}
          onClick={() => setAddOpen(true)}
        >
          <span className="text-xl leading-none">+</span>
          <span className="text-sm font-medium">Add platform</span>
        </button>
        {!items.length && <div className="col-span-3 card p-10 text-center text-sm" style={{ color: 'var(--text-faint)' }}>No platforms configured</div>}
      </div>

      <Modal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add platform"
        footer={
          <>
            <button className="btn-outline" onClick={() => setAddOpen(false)}>Cancel</button>
            <button className="btn-accent" disabled={!newName.trim() || create.isPending} onClick={() => create.mutate(newName.trim())}>
              {create.isPending ? 'Adding…' : 'Add'}
            </button>
          </>
        }
      >
        <label className="text-xs font-medium block mb-1" style={{ color: 'var(--text-muted)' }}>Platform name</label>
        <input className="w-full" placeholder="e.g. Jumia Food" value={newName} onChange={(e) => setNewName(e.target.value)} autoFocus />
        {create.error && <ErrorBox error={create.error} />}
      </Modal>
    </div>
  );
}
